// BannersList.js
import { useState, useEffect } from "react";
import {
  getBannersList,
  createBanner,
  updateBanner,
  toggleBanner,
  deleteBanner,
} from "../../service/Mainservice";
import Loader from "../../Loader/Loader";
import Modal from "../Model/Model";
import "./Banners.css";

// MUI Icons
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ToggleOnIcon from "@mui/icons-material/ToggleOn";
import ToggleOffIcon from "@mui/icons-material/ToggleOff";

const BannersList = () => {
  const [loading, setLoading] = useState(true);
  const [banners, setBanners] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingBanner, setEditingBanner] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    image: "",
    vendorId: "",
    productId: "",
    startDate: "",
    endDate: "",
    isActive: true,
  });

  const fetchBanners = async () => {
    setLoading(true);
    try {
      const res = await getBannersList({ activeOnly: false });
      setBanners(res.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBanners();
  }, []);

  const openCreateModal = () => {
    setEditingBanner(null);
    setFormData({
      title: "",
      image: "",
      vendorId: "",
      productId: "",
      startDate: "",
      endDate: "",
      isActive: true,
    });
    setModalOpen(true);
  };

  const openEditModal = (banner) => {
    setEditingBanner(banner);
    setFormData({
      title: banner.title || "",
      image: banner.image || "",
      vendorId: banner.vendorId || "",
      productId: banner.productId || "",
      startDate: banner.startDate ? banner.startDate.split("T")[0] : "",
      endDate: banner.endDate ? banner.endDate.split("T")[0] : "",
      isActive: banner.isActive,
    });
    setModalOpen(true);
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const payload = {
        ...formData,
        vendorId: formData.vendorId || undefined,
        productId: formData.productId
          ? parseInt(formData.productId)
          : undefined,
        startDate: formData.startDate || undefined,
        endDate: formData.endDate || undefined,
      };
      if (editingBanner) {
        await updateBanner({ id: editingBanner.id, ...payload });
      } else {
        await createBanner(payload);
      }
      setModalOpen(false);
      fetchBanners();
    } catch (err) {
      alert(err.response?.data?.message || "Operation failed.");
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (id) => {
    try {
      await toggleBanner({ id });
      fetchBanners();
    } catch (err) {
      alert(err.response?.data?.message || "Toggle failed.");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this banner?")) return;
    try {
      await deleteBanner({ id });
      fetchBanners();
    } catch (err) {
      alert(err.response?.data?.message || "Delete failed.");
    }
  };

  return (
    <div className="banners-page">
      <div className="page-actions">
        <button className="btn-primary" onClick={openCreateModal}>
          <AddIcon /> Add Banner
        </button>
      </div>

      {loading ? (
        <Loader />
      ) : (
        <div className="banners-grid">
          {banners.length === 0 ? (
            <p className="no-data">No banners found.</p>
          ) : (
            banners.map((b) => (
              <div key={b.id} className="banner-card">
                <div className="banner-image">
                  <img src={b?.image} alt={b.title} />
                  <span
                    className={`banner-status ${b.isActive ? "active" : "inactive"}`}
                  >
                    {b.isActive ? "Active" : "Inactive"}
                  </span>
                </div>
                <div className="banner-info">
                  <h4>{b.title}</h4>
                  <p>{b.vendorId ? `Store: ${b.vendorId}` : "No store"}</p>
                  {b.startDate && b.endDate && (
                    <p className="banner-dates">
                      {new Date(b.startDate).toLocaleDateString()} -{" "}
                      {new Date(b.endDate).toLocaleDateString()}
                    </p>
                  )}
                  <div className="banner-actions">
                    <button
                      className="btn-outline btn-sm"
                      onClick={() => openEditModal(b)}
                    >
                      <EditIcon fontSize="small" />
                    </button>
                    <button
                      className="btn-outline btn-sm"
                      onClick={() => handleToggle(b.id)}
                    >
                      {b.isActive ? (
                        <ToggleOffIcon fontSize="small" />
                      ) : (
                        <ToggleOnIcon fontSize="small" />
                      )}
                    </button>
                    <button
                      className="btn-danger btn-sm"
                      onClick={() => handleDelete(b.id)}
                    >
                      <DeleteIcon fontSize="small" />
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Create/Edit Modal */}
      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editingBanner ? "Edit Banner" : "Add Banner"}
      >
        <div className="banner-form">
          <div className="form-group">
            <label>Title *</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) =>
                setFormData({ ...formData, title: e.target.value })
              }
              placeholder="e.g. Weekend Sale"
            />
          </div>
          <div className="form-group">
            <label>Image URL *</label>
            <input
              type="text"
              value={formData.image}
              onChange={(e) =>
                setFormData({ ...formData, image: e.target.value })
              }
              placeholder="https://example.com/banner.jpg"
            />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Vendor ID</label>
              <input
                type="text"
                value={formData.vendorId}
                onChange={(e) =>
                  setFormData({ ...formData, vendorId: e.target.value })
                }
                placeholder="Store ID"
              />
            </div>
            <div className="form-group">
              <label>Product ID</label>
              <input
                type="text"
                value={formData.productId}
                onChange={(e) =>
                  setFormData({ ...formData, productId: e.target.value })
                }
                placeholder="Product ID"
              />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Start Date</label>
              <input
                type="date"
                value={formData.startDate}
                onChange={(e) =>
                  setFormData({ ...formData, startDate: e.target.value })
                }
              />
            </div>
            <div className="form-group">
              <label>End Date</label>
              <input
                type="date"
                value={formData.endDate}
                onChange={(e) =>
                  setFormData({ ...formData, endDate: e.target.value })
                }
              />
            </div>
          </div>
          <div className="form-group checkbox-group">
            <label>
              <input
                type="checkbox"
                checked={formData.isActive}
                onChange={(e) =>
                  setFormData({ ...formData, isActive: e.target.checked })
                }
              />
              Active
            </label>
          </div>
          <div className="modal-actions">
            <button
              className="btn-secondary"
              onClick={() => setModalOpen(false)}
            >
              Cancel
            </button>
            <button
              className="btn-primary"
              onClick={handleSubmit}
              disabled={loading}
            >
              {loading ? "Saving..." : editingBanner ? "Update" : "Create"}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default BannersList;
